import { StandardEvents } from '@shopify/events';

function streetohvRegistrationCartValid(cart) {
  if (!Array.isArray(cart.items) || !cart.items.length) return false;
  const handles = new Set(['car-suv-registration-service','truck-registration-service','sxs-utv-atv-registration-service','rv-motorhome-registration-service','travel-trailer-registration-service','boat-watercraft-registration-service','snowmobile-registration-service','trailer-registration-service','street-motorcycle-registration-service']);
  const registrations = cart.items.filter(item => handles.has(item.handle));
  const fees = cart.items.filter(item => item.properties?.['Linked vehicle']);
  for (const item of registrations) {
    const p = item.properties || {};
    const group = p['_Registration group'];
    if (p.Qualifier !== 'Any vehicle registration' || !group || item.quantity !== 1) return false;
    let required;
    try { required = JSON.parse(p['_Required fee variants']); } catch { return false; }
    if (!Array.isArray(required) || required.some(id => typeof id !== 'string')) return false;
    const linked = fees.filter(fee => fee.properties['_Registration group'] === group);
    if (linked.length !== required.length || new Set(required).size !== required.length) return false;
    if (required.some(id => linked.filter(fee => String(fee.variant_id) === id && fee.quantity === 1 && String(fee.properties['Linked vehicle']) === String(item.variant_id)).length !== 1)) return false;
    const parent = p['_Shared LLC registration'];
    if (parent && !registrations.concat(cart.items.filter(x => /^(dirtbike-motorcycle|emoto-surron)/.test(x.handle))).some(other => other.key === parent && other.key !== item.key && /Montana/.test([other.variant_title,...(other.variant_options || [])].join(' ')) && !/South Dakota/.test([other.variant_title,...(other.variant_options || [])].join(' ')))) return false;
  }
  return fees.every(fee => registrations.some(item => item.properties['_Registration group'] === fee.properties['_Registration group'] && String(item.variant_id) === String(fee.properties['Linked vehicle'])));
}

class StreetohvCartConsent extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.pending = false;
    this.submitting = false;
    const options = {signal:this.abort.signal};
    this.input = this.querySelector('[data-policy-agreement]');
    this.button = this.querySelector('[name="checkout"]');
    this.error = this.querySelector('[data-policy-error]');
    if (!this.input || !this.button || !this.error) return;
    this.input.checked = false;
    this.button.disabled = true;
    this.cartValid = false;
    this.checking = true;
    this.cartCheck = this.refreshCart();
    document.addEventListener(StandardEvents.cartLinesUpdate, async event => {
      this.checking = true;
      this.cartValid = false;
      this.button.disabled = true;
      try { await event.promise; } catch { }
      if (this.isConnected) await this.refreshCart();
    }, options);
    this.input.addEventListener('change', () => {
      this.button.disabled = !this.input.checked || this.pending || this.checking || !this.cartValid;
      this.error.hidden = this.cartValid;
    }, options);
    this.button.addEventListener('click', event => {
      event.preventDefault();
      this.checkout();
    }, options);
    document.addEventListener('submit', event => {
      if (event.submitter !== this.button || this.submitting) return;
      event.preventDefault();
      this.checkout();
    }, {...options,capture:true});
  }
  disconnectedCallback() { this.abort?.abort(); }
  async refreshCart() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {headers:{Accept:'application/json'},cache:'no-store',signal:this.abort.signal});
      if (!response.ok) throw new Error('cart_load_failed');
      const cart = await response.json();
      if (!this.isConnected) return;
      this.cartValid = streetohvRegistrationCartValid(cart);
      this.error.textContent = this.dataset.cartError;
      this.error.hidden = this.cartValid;
    } catch (error) {
      if (error.name === 'AbortError' || !this.isConnected) return;
      this.cartValid = false;
      this.error.textContent = this.dataset.saveError;
      this.error.hidden = false;
    } finally {
      this.checking = false;
      this.button.disabled = !this.input.checked || this.pending || !this.cartValid;
    }
  }
  async checkout() {
    if (this.pending) return;
    if (this.checking || !this.cartValid) return;
    if (!this.input.checked) {
      this.error.textContent = this.dataset.requiredMessage;
      this.error.hidden = false;
      this.input.focus();
      return;
    }
    const form = this.button.form;
    if (!form) return;
    this.pending = true;
    this.button.disabled = true;
    this.error.hidden = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const attributes = {
        'Terms and return policy accepted':'Yes',
        'Policy acceptance time (UTC)':new Date().toISOString(),
        'Accepted terms URL':this.querySelector('[data-terms-url]').href,
        'Accepted return policy URL':this.querySelector('[data-returns-url]').href
      };
      const response = await fetch(`${root}cart/update.js`, {
        method:'POST',headers:{'Content-Type':'application/json','Accept':'application/json'},
        body:JSON.stringify({attributes})
      });
      if (!response.ok) throw new Error('consent_save_failed');
      const cart = await response.json();
      if (!streetohvRegistrationCartValid(cart)) {
        this.cartValid = false;
        this.error.textContent = this.dataset.cartError;
        this.error.hidden = false;
        return;
      }
      if (cart.attributes?.['Terms and return policy accepted'] !== 'Yes') throw new Error('consent_save_failed');
      if (!this.isConnected || !this.input.checked) throw new Error('consent_changed');
      this.submitting = true;
      this.button.disabled = false;
      form.requestSubmit(this.button);
      this.submitting = false;
    } catch {
      this.error.textContent = this.dataset.saveError;
      this.error.hidden = false;
    } finally {
      this.pending = false;
      this.button.disabled = !this.input.checked || !this.cartValid;
    }
  }
}
if (!customElements.get('streetohv-cart-consent')) customElements.define('streetohv-cart-consent',StreetohvCartConsent);
